/**
 * API Configuration
 *
 * This file manages the routing between the new Vercel API routes
 * and the legacy backend API during the migration period.
 *
 * Routes that have been migrated to Vercel use relative paths (e.g., /api/...)
 * Routes still on the legacy backend use NEXT_PUBLIC_API_URL
 */

// Legacy backend URL (for routes not yet migrated)
export const LEGACY_API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';

// Feature flag to control migration rollout
export const USE_NEW_API = process.env.NEXT_PUBLIC_USE_NEW_API === 'true';

/**
 * Routes that have been migrated to Vercel API routes
 * These use relative paths and run on the same domain
 */
const MIGRATED_ROUTES = [
  // Transcriptions API
  '/api/transcriptions',
  '/api/chat',
  '/api/preferences',
  '/api/export',
  '/api/search',
  '/api/shared',
  '/api/sharing',
  '/api/content-types',
  // Custom Templates
  '/api/custom-templates',
  // Referrals
  '/api/referrals',
  // Billing API
  '/api/billing/plans',
  '/api/billing/subscription',
  '/api/billing/usage',
  '/api/billing/invoices',
  '/api/billing/extra-time',
  '/api/billing/webhooks',
  '/api/billing/report',
  '/api/billing/user',
  // Integration API
  '/api/integrations',
  // Plans API (public)
  '/api/plans',
  '/api/currencies',
] as const;

/**
 * Routes that must stay on the legacy backend
 * These require features not available in Vercel (WebSockets, FFmpeg, etc.)
 */
const LEGACY_ONLY_ROUTES = [
  '/api/live-streaming',     // WebSockets for real-time transcription
  '/api/upload',             // File upload with FFmpeg processing
  '/api/cloud-storage',      // Cloud storage OAuth callbacks
  '/api/youtube',            // YouTube processing with yt-dlp
  '/api/process',            // File processing with FFmpeg
  '/api/realtime',           // Real-time transcription tokens
  '/api/r2',                 // R2 upload management
  '/api/download',           // Audio file download from R2
  '/api/stream',             // Audio streaming from R2
  '/api/reprocess',          // Reprocess job (BullMQ)
  '/api/waitlist',           // Waitlist (can be migrated later)
  '/api/onboarding',         // Onboarding flow
  '/api/webhooks/assemblyai', // AssemblyAI webhooks
] as const;

/**
 * Check if a route has been migrated to Vercel
 */
export function isMigratedRoute(path: string): boolean {
  if (!USE_NEW_API) {
    return false;
  }

  return MIGRATED_ROUTES.some((route) => path.startsWith(route));
}

/**
 * Check if a route must stay on legacy backend
 */
export function isLegacyOnlyRoute(path: string): boolean {
  return LEGACY_ONLY_ROUTES.some((route) => path.startsWith(route));
}

/**
 * Get the appropriate base URL for an API path
 * Returns empty string for migrated routes (relative path)
 * Returns LEGACY_API_URL for legacy routes
 */
export function getApiBaseUrl(path: string): string {
  // Legacy-only routes always use the old backend
  if (isLegacyOnlyRoute(path)) {
    return LEGACY_API_URL;
  }

  // Migrated routes use relative paths (same domain)
  if (isMigratedRoute(path)) {
    return '';
  }

  // Default to legacy API for non-migrated routes
  return LEGACY_API_URL;
}

/**
 * Build a full API URL for the given path
 */
export function buildApiUrl(path: string): string {
  const baseUrl = getApiBaseUrl(path);
  return `${baseUrl}${path}`;
}

/**
 * API client helper with automatic base URL selection
 */
export async function apiFetch(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  const url = buildApiUrl(path);
  return fetch(url, options);
}

/**
 * API client helper with authentication
 */
export async function authenticatedFetch(
  path: string,
  token: string,
  options: RequestInit = {}
): Promise<Response> {
  const url = buildApiUrl(path);
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
}
